// Filtri condivisi dalle viste di approvazione (incarichi, servizi fiduciari, hub).
// Tutti facoltativi e combinati in AND:
//   - ritardo d'inizio entro N minuti rispetto al pianificato
//   - km: <, >, = un valore
//   - spese: <, >, = un valore
// I caller ricavano `{ ritardoMin?, km?, spese? }` per ogni record e lo passano
// a `passesApprovazioneFilters`.
import React from 'react';
import { Filter, RotateCcw, Loader2, CheckCircle2 } from 'lucide-react';
import { Modal } from './Modal';

export type ComparisonOp = 'lt' | 'gt' | 'eq';

// Stato turno/report. 'TUTTI' (o assente) = nessun filtro; se l'item non
// porta lo stato il filtro non ha effetto.
export type StatoFilterValue =
    | 'TUTTI'
    | 'PIANIFICATI'
    | 'DA_APPROVARE'
    | 'APPROVATI';

export interface ApprovazioneFiltersValue {
    ritardoMaxMin?: number | '';
    kmOp?: ComparisonOp;
    kmVal?: number | '';
    speseOp?: ComparisonOp;
    speseVal?: number | '';
    stato?: StatoFilterValue;
}

export const EMPTY_APPROVAZIONE_FILTERS: ApprovazioneFiltersValue = {
    ritardoMaxMin: '',
    kmOp: 'lt',
    kmVal: '',
    speseOp: 'lt',
    speseVal: '',
    stato: 'TUTTI',
};

const OP_LABEL: Record<ComparisonOp, string> = { lt: '<', gt: '>', eq: '=' };

interface Props {
    value: ApprovazioneFiltersValue;
    onChange: (v: ApprovazioneFiltersValue) => void;
    // Conteggio visibili/totali mostrato accanto al titolo ("3 di 18 visibili").
    matchedCount?: number;
    totalCount?: number;
    // Select dello stato: serve solo ai turni fiduciari, dove esistono record
    // ancora pianificati.
    showStatoFilter?: boolean;
}

export const ApprovazioniFilters: React.FC<Props> = ({ value, onChange, matchedCount, totalCount, showStatoFilter }) => {
    const set = <K extends keyof ApprovazioneFiltersValue>(k: K, v: ApprovazioneFiltersValue[K]) =>
        onChange({ ...value, [k]: v });

    const active = isFiltering(value);
    const reset = () => onChange(EMPTY_APPROVAZIONE_FILTERS);

    return (
        <div className={`rounded-2xl border shadow-sm bg-white ${active ? 'border-indigo-200' : 'border-slate-200'}`}>
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`p-1.5 rounded-lg ${active ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                        <Filter size={14}/>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-600">Filtri</span>
                    {typeof matchedCount === 'number' && typeof totalCount === 'number' && (
                        <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                            {matchedCount} / {totalCount}
                        </span>
                    )}
                </div>
                {active && (
                    <button
                        onClick={reset}
                        className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100 transition-colors"
                        title="Azzera tutti i filtri"
                    >
                        <RotateCcw size={11}/> Reset
                    </button>
                )}
            </div>
            <div className={`p-3 grid grid-cols-1 ${showStatoFilter ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
                {/* Stato turno: filtro categoriale (solo viste con concetto di pianificato) */}
                {showStatoFilter && (
                    <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                            Stato
                        </label>
                        <select
                            value={value.stato || 'TUTTI'}
                            onChange={e => set('stato', e.target.value as StatoFilterValue)}
                            className="w-full p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none bg-white focus:ring-2 focus:ring-indigo-300"
                        >
                            <option value="TUTTI">Tutti</option>
                            <option value="PIANIFICATI">Pianificati</option>
                            <option value="DA_APPROVARE">Da approvare</option>
                            <option value="APPROVATI">Approvati</option>
                        </select>
                    </div>
                )}

                {/* Ritardo inizio */}
                <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                        Inizio con ritardo entro
                    </label>
                    <div className="flex items-center gap-1">
                        <input
                            type="number" min={0} step={1}
                            value={value.ritardoMaxMin ?? ''}
                            onChange={e => set('ritardoMaxMin', e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="es. 15"
                            className="w-full p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                        <span className="text-[10px] font-bold text-slate-400 shrink-0">min</span>
                    </div>
                </div>

                {/* Km */}
                <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                        Km
                    </label>
                    <div className="flex items-center gap-1">
                        <select
                            value={value.kmOp || 'lt'}
                            onChange={e => set('kmOp', e.target.value as ComparisonOp)}
                            className="p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none bg-white"
                        >
                            <option value="lt">{OP_LABEL.lt}</option>
                            <option value="gt">{OP_LABEL.gt}</option>
                            <option value="eq">{OP_LABEL.eq}</option>
                        </select>
                        <input
                            type="number" min={0} step={1}
                            value={value.kmVal ?? ''}
                            onChange={e => set('kmVal', e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="es. 50"
                            className="w-full p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                    </div>
                </div>

                {/* Spese */}
                <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1">
                        Spese (€)
                    </label>
                    <div className="flex items-center gap-1">
                        <select
                            value={value.speseOp || 'lt'}
                            onChange={e => set('speseOp', e.target.value as ComparisonOp)}
                            className="p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none bg-white"
                        >
                            <option value="lt">{OP_LABEL.lt}</option>
                            <option value="gt">{OP_LABEL.gt}</option>
                            <option value="eq">{OP_LABEL.eq}</option>
                        </select>
                        <input
                            type="number" min={0} step={0.01}
                            value={value.speseVal ?? ''}
                            onChange={e => set('speseVal', e.target.value === '' ? '' : Number(e.target.value))}
                            placeholder="es. 20"
                            className="w-full p-2 border border-slate-200 rounded-lg text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-300"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

/** Vero quando almeno un filtro è valorizzato (numero valido / scelto un op). */
export const isFiltering = (v: ApprovazioneFiltersValue): boolean => {
    const isNum = (x: any) => x !== '' && x !== null && x !== undefined && !isNaN(Number(x));
    const statoActive = !!v.stato && v.stato !== 'TUTTI';
    return isNum(v.ritardoMaxMin) || isNum(v.kmVal) || isNum(v.speseVal) || statoActive;
};

/**
 * Valuta i filtri su un singolo item.
 *
 * Con un filtro numerico attivo, i record senza il dato corrispondente
 * (null/undefined) vengono esclusi: non si può dire se lo rispettano.
 * Il filtro `stato` si applica solo agli item che hanno `stato` valorizzato.
 */
export const passesApprovazioneFilters = (
    v: ApprovazioneFiltersValue,
    item: {
        ritardoMin?: number | null;
        km?: number | null;
        spese?: number | null;
        stato?: StatoFilterValue;
    },
): boolean => {
    const isNum = (x: any) => x !== '' && x !== null && x !== undefined && !isNaN(Number(x));

    // Filtro stato: ignorato per gli item senza `stato`.
    if (v.stato && v.stato !== 'TUTTI' && item.stato) {
        if (item.stato !== v.stato) return false;
    }

    // "Entro X min" = `0 <= ritardo <= X`; dato mancante → escluso.
    if (isNum(v.ritardoMaxMin)) {
        const max = Number(v.ritardoMaxMin);
        if (item.ritardoMin == null || isNaN(Number(item.ritardoMin))) {
            return false;
        }
        const r = Number(item.ritardoMin);
        if (!(r >= 0 && r <= max)) return false;
    }

    // Km mancanti → escluso; 0 km è invece un valore valido.
    if (isNum(v.kmVal)) {
        if (item.km == null) return false;
        const target = Number(v.kmVal);
        const km = Number(item.km);
        if (!compareOp(km, v.kmOp || 'lt', target)) return false;
    }

    // Spese: stessa logica dei km.
    if (isNum(v.speseVal)) {
        if (item.spese == null) return false;
        const target = Number(v.speseVal);
        const spese = Number(item.spese);
        if (!compareOp(spese, v.speseOp || 'lt', target)) return false;
    }

    return true;
};

const compareOp = (a: number, op: ComparisonOp, b: number): boolean => {
    if (op === 'lt') return a < b;
    if (op === 'gt') return a > b;
    // 'eq' con tolleranza 0.001 per i float.
    return Math.abs(a - b) < 0.001;
};

/**
 * Ritardo d'inizio di un turno fiduciario: `effettivoInizio` meno
 * `dataTurno + oraInizio`. null se manca uno dei due.
 */
export const ritardoMinutiTurno = (turno: { dataTurno?: string | null; oraInizio?: string | null; effettivoInizio?: string | null }): number | null => {
    if (!turno?.dataTurno || !turno?.oraInizio || !turno?.effettivoInizio) return null;
    const [hh, mm] = String(turno.oraInizio).split(':').map(Number);
    if (isNaN(hh) || isNaN(mm)) return null;
    // Orario pianificato in ora locale.
    const planned = new Date(`${turno.dataTurno}T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00`);
    const effettivo = new Date(turno.effettivoInizio);
    if (isNaN(planned.getTime()) || isNaN(effettivo.getTime())) return null;
    return Math.round((effettivo.getTime() - planned.getTime()) / 60000);
};

/**
 * Ritardo d'inizio di un report incarico: primo `sessioniSelezionate[].startTime`
 * meno `incaricoDataInizio`. null se i dati non bastano.
 */
export const ritardoMinutiReportIncarico = (
    report: { sessioniSelezionate?: Array<{ startTime?: string | null }> | null },
    incaricoDataInizio?: string | null,
): number | null => {
    if (!incaricoDataInizio) return null;
    const firstStart = report?.sessioniSelezionate?.[0]?.startTime;
    if (!firstStart) return null;
    const planned = new Date(incaricoDataInizio);
    const effettivo = new Date(firstStart);
    if (isNaN(planned.getTime()) || isNaN(effettivo.getTime())) return null;
    return Math.round((effettivo.getTime() - planned.getTime()) / 60000);
};

/** Helper per sommare le spese di un report (valore inviato o approvato). */
export const totaleSpeseReport = (
    spese: Array<{ importoApprovato?: number | string | null; importo?: number | string | null }> | null | undefined,
): number => {
    if (!Array.isArray(spese)) return 0;
    return spese.reduce((acc, s) => {
        const v = s.importoApprovato ?? s.importo ?? 0;
        const n = Number(v);
        return acc + (isNaN(n) ? 0 : n);
    }, 0);
};

/** Etichette leggibili dei filtri attivi (es. per BulkApproveModal). */
export const describeApprovazioneFilters = (v: ApprovazioneFiltersValue): string[] => {
    const out: string[] = [];
    const isNum = (x: any) => x !== '' && x !== null && x !== undefined && !isNaN(Number(x));
    if (isNum(v.ritardoMaxMin)) out.push(`Inizio con ritardo entro ${Number(v.ritardoMaxMin)} min`);
    if (isNum(v.kmVal)) out.push(`Km ${opLabel(v.kmOp)} ${Number(v.kmVal)}`);
    if (isNum(v.speseVal)) out.push(`Spese ${opLabel(v.speseOp)} € ${Number(v.speseVal).toFixed(2)}`);
    return out;
};
const opLabel = (op?: ComparisonOp) => op === 'gt' ? '>' : op === 'eq' ? '=' : '<';

// Modale di conferma "Approva tutti", con il numero di elementi e i filtri attivi.
export interface BulkApproveModalProps {
    count: number;
    filters: ApprovazioneFiltersValue;
    saving: boolean;
    progress?: { done: number; total: number } | null;
    /** Etichetta del soggetto al singolare/plurale, es. "report" / "turno". */
    label?: string;
    onCancel: () => void;
    onConfirm: () => void;
}

export const BulkApproveModal: React.FC<BulkApproveModalProps> = ({
    count, filters, saving, progress, label = 'report', onCancel, onConfirm,
}) => {
    const summary = describeApprovazioneFilters(filters);
    return (
        <Modal title="Approva tutti" onClose={onCancel}>
            <div className="p-1 space-y-3 text-sm">
                <p className="text-slate-700">
                    Sicuro di voler approvare <strong className="text-emerald-700">{count}</strong>{' '}
                    {label}?
                </p>
                {summary.length > 0 ? (
                    <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs">
                        <p className="font-bold text-indigo-700 mb-1">Con filtri</p>
                        <ul className="list-disc pl-4 text-indigo-800 space-y-0.5">
                            {summary.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                    </div>
                ) : (
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-800">
                        Nessun filtro attivo: verranno approvati <strong>tutti</strong> i {label} ancora in attesa.
                    </div>
                )}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-600 leading-relaxed">
                    Verranno applicati i <strong>valori inviati dal collaboratore</strong>
                    {' '}(km, ore extra, importi spese). Eventuali rettifiche fatte in questa
                    schermata e non salvate <strong>non saranno applicate</strong>: per quelle
                    usa il tasto "Approva" del singolo report.
                </div>
                {progress && (
                    <p className="text-[11px] text-slate-500 font-mono">
                        {progress.done}/{progress.total} approvati…
                    </p>
                )}
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button onClick={onCancel} disabled={saving}
                            className="px-3 py-2 text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-50">
                        Annulla
                    </button>
                    <button onClick={onConfirm} disabled={saving || count === 0}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg shadow-sm disabled:opacity-50">
                        {saving ? <Loader2 size={13} className="animate-spin"/> : <CheckCircle2 size={13}/>}
                        Approva {count}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
